import { useCallback, useRef, useState } from "react";
import { useAudioEngine } from "./audio-engine";

// "Replay this moment" (Sources): plays a segment's [start, end] through the
// page's one audio engine, the same one as the transcript drawer's player.

export type ReplayState = "idle" | "loading" | "playing" | "error";

export function useMomentReplay(
  audioPath: string | null,
  startMs: number | null,
  endMs: number | null,
) {
  const engine = useAudioEngine();
  const [requested, setRequested] = useState(false);
  const [failed, setFailed] = useState(false);
  const key = useRef("");
  const moment = `${audioPath}:${startMs}:${endMs}`;
  if (key.current !== moment) {
    key.current = moment;
    if (requested) setRequested(false);
    if (failed) setFailed(false);
  }
  const ours =
    requested &&
    engine.path === audioPath &&
    startMs !== null &&
    endMs !== null;
  const from = Math.max(0, (startMs ?? 0) - 250) / 1000;
  const to = ((endMs ?? 0) + 400) / 1000;
  const inRange =
    ours && engine.currentTime >= from - 0.05 && engine.currentTime <= to;
  const state: ReplayState = failed
    ? "error"
    : ours && engine.status === "loading"
      ? "loading"
      : ours && engine.playing && inRange
        ? "playing"
        : "idle";
  const progress = inRange
    ? Math.min(1, Math.max(0, (engine.currentTime - from) / (to - from)))
    : 0;
  const play = useCallback(async () => {
    if (!audioPath || startMs === null || endMs === null) return;
    setRequested(true);
    setFailed(false);
    const ok = await useAudioEngine
      .getState()
      .playRange(audioPath, startMs, endMs);
    if (!ok) setFailed(true);
  }, [audioPath, startMs, endMs]);
  const stop = useCallback(() => {
    useAudioEngine.getState().pause();
  }, []);
  return { state, progress, play, stop };
}
